import { z } from 'zod';
import {
  esZonaValida,
  LARGO_MAXIMO_DOCUMENTO,
  LARGO_MINIMO_DOCUMENTO,
  normalizarDocumento,
} from '@planilla/dominio';

/**
 * ESQUEMAS DE ENTRADA
 * ===================
 *
 * La API valida con estos y el front arma sus formularios con los mismos: un campo
 * obligatorio no puede serlo en un lado y opcional en el otro.
 *
 * Los mensajes están en criollo y pensados para quien está cargando —muchas veces de pie, en
 * un mostrador—: la API los devuelve tal cual y el front los muestra sin traducir.
 */

const texto = (nombre: string, max = 200) =>
  z
    .string({ required_error: `Falta ${nombre}.` })
    .trim()
    .min(1, `Falta ${nombre}.`)
    .max(max, `${nombre} es demasiado largo.`);

const textoOpcional = (max = 500) =>
  z.string().trim().max(max).optional().nullable().transform((v) => (v ? v : null));

export const uuid = z.string().uuid('Identificador inválido.');
export const fecha = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha tiene que tener el formato AAAA-MM-DD.');

/**
 * DINERO EN LA ENTRADA
 *
 * Entero en la unidad mínima. Se acepta número o cadena de dígitos a propósito: la API
 * DEVUELVE los montos como cadena (BigInt serializado), así que el front tiene que poder
 * mandar de vuelta lo que recibió sin convertirlo y sin que un `parseFloat` de por medio le
 * meta un decimal.
 */
export const importe = z
  .union([
    z.number({ invalid_type_error: 'El monto tiene que ser un número.' }),
    z.string().trim().regex(/^\d+$/, 'El monto va en enteros, sin puntos ni comas.'),
  ])
  .transform((valor, ctx) => {
    const numero = typeof valor === 'number' ? valor : Number(valor);
    if (!Number.isInteger(numero)) {
      ctx.addIssue({ code: 'custom', message: 'El monto no lleva decimales: el guaraní no tiene centavos.' });
      return z.NEVER;
    }
    if (numero < 0) {
      ctx.addIssue({ code: 'custom', message: 'El monto no puede ser negativo.' });
      return z.NEVER;
    }
    if (!Number.isSafeInteger(numero)) {
      ctx.addIssue({ code: 'custom', message: 'El monto es demasiado grande.' });
      return z.NEVER;
    }
    return numero;
  });

export const enteroNoNegativo = z.number().int().nonnegative();

/**
 * CÉDULA EN LA ENTRADA
 *
 * Se normaliza en el borde y con la MISMA función que usa el resto del sistema: si el front
 * manda "1.234.567" y la base guardó "1234567", una búsqueda literal no encuentra a nadie y
 * el mostrador dice "no está registrado" sobre alguien que sí está. Ese falso negativo
 * termina en un segundo alta y en un segundo cobro.
 */
export const documento = z
  .string({ required_error: 'Falta la cédula.' })
  .trim()
  .transform(normalizarDocumento)
  .refine(
    (v) => v.length >= LARGO_MINIMO_DOCUMENTO && v.length <= LARGO_MAXIMO_DOCUMENTO,
    `La cédula tiene que tener entre ${LARGO_MINIMO_DOCUMENTO} y ${LARGO_MAXIMO_DOCUMENTO} dígitos.`,
  );

/**
 * Si el nombre lo propuso el padrón de la DNIT y la persona lo aceptó tal cual.
 *
 * Existe para que `origenDatos` sirva de verdad: sin esto, todo lo cargado desde una pantalla queda
 * como MANUAL y no hay forma de revisar después "solo lo que vino sugerido" —que es justamente el
 * caso que hay que poder revisar, porque el padrón es ayuda y no fuente de verdad—.
 *
 * Es opcional y por defecto false: quien no lo manda queda como carga a mano, que es lo correcto.
 */
const tomadoDelPadron = z.boolean().default(false);

export const paginacion = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
  /** Busca por cédula Y por nombre: quien atiende no sabe de antemano qué le van a decir. */
  q: z.string().trim().max(100).optional(),
});

/** Los booleanos que llegan por query string vienen como texto. */
const booleanoDeQuery = z
  .enum(['true', 'false'])
  .optional()
  .transform((v) => (v === undefined ? undefined : v === 'true'));

// --- Sesión ---------------------------------------------------------------------------

/**
 * Se ingresa con lo que la persona recuerde: nombre de usuario, correo o cédula. No se exige
 * formato de correo: una sucursal quiere usuarios cortos ("caja1", "mostrador").
 */
export const esquemaIngreso = z.object({
  identificador: z.string().trim().toLowerCase().min(1, 'Escribí tu usuario, correo o cédula.'),
  clave: z.string().min(1, 'Escribí tu contraseña.'),
  /** Si la persona trabaja en más de una entidad, con cuál entra. */
  entidadId: uuid.optional(),
});

export const esquemaRefresco = z.object({
  refresh: z.string().min(10, 'Falta el token de sesión.'),
});

export const esquemaCambioEntidad = z.object({ entidadId: uuid });

export const esquemaCambioClave = z.object({
  claveActual: z.string().min(1, 'Escribí tu contraseña actual.'),
  claveNueva: z.string().min(10, 'La contraseña nueva tiene que tener al menos 10 caracteres.'),
});

/** Nombre de usuario o correo: minúsculas, sin espacios. */
export const identificadorUsuario = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'El usuario tiene que tener al menos 3 caracteres.')
  .max(120)
  .regex(/^[a-z0-9._@+-]+$/, 'El usuario va sin espacios: letras, números, punto, guion o @.')
  .refine((v) => !/^\d+$/.test(v), 'El usuario no puede ser solo números: para eso está la cédula.');

export const documentoOpcional = z
  .string()
  .trim()
  .transform(normalizarDocumento)
  .refine((v) => v === '' || (v.length >= LARGO_MINIMO_DOCUMENTO && v.length <= LARGO_MAXIMO_DOCUMENTO), 'La cédula no es válida.')
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

// --- Mostrador ------------------------------------------------------------------------

export const esquemaConsultaMostrador = z.object({
  documento,
  actividadId: uuid,
});

/**
 * El pago del mostrador. El monto viaja aunque el sistema ya lo sepa: es lo que el cajero
 * está viendo en pantalla, y si no coincide con la participación se rechaza en vez de pagar
 * otra cifra en silencio (ver `asegurarMontoDelPago` en el dominio).
 */
export const esquemaPagoMostrador = z.object({
  participacionId: uuid,
  monto: importe,
  medioPagoId: uuid.optional().nullable(),
  referencia: textoOpcional(100),
  notas: textoOpcional(500),
  /** Si la entidad exige visto bueno y el monto difiere del de la actividad. */
  vistoBuenoPorId: uuid.optional().nullable(),
});
export type EntradaPagoMostrador = z.infer<typeof esquemaPagoMostrador>;

/**
 * Alta en el mostrador: la persona trabajó y no está en la lista. Solo si la entidad lo permite
 * (`permiteAltaEnMostrador`). Queda auditada con quién y cuándo; el motivo es opcional
 * (Rodrigo, 2026-09-26: el mostrador también sirve para inscribir, no es una excepción).
 */
export const esquemaInscribirYPagar = z.object({
  actividadId: uuid,
  documento,
  nombre: texto('el nombre', 120),
  apellido: textoOpcional(120),
  telefono: textoOpcional(40),
  zonaId: uuid.optional().nullable(),
  tomadoDelPadron,
  monto: importe,
  /** Solo hace falta si el monto difiere del de la actividad. */
  motivoMonto: textoOpcional(300),
  medioPagoId: uuid.optional().nullable(),
  referencia: textoOpcional(100),
  notas: textoOpcional(500),
  vistoBuenoPorId: uuid.optional().nullable(),
});
export type EntradaInscribirYPagar = z.infer<typeof esquemaInscribirYPagar>;

/** Agregar a la lista desde el mostrador SIN pagar todavía: se le paga cuando venga. */
export const esquemaInscribirEnMostrador = z.object({
  actividadId: uuid,
  documento,
  nombre: texto('el nombre', 120),
  apellido: textoOpcional(120),
  telefono: textoOpcional(40),
  zonaId: uuid.optional().nullable(),
  tomadoDelPadron,
});
export type EntradaInscribirEnMostrador = z.infer<typeof esquemaInscribirEnMostrador>;

// --- Integrantes -----------------------------------------------------------------------

export const esquemaIntegrante = z.object({
  documento,
  nombre: texto('el nombre', 120),
  apellido: textoOpcional(120),
  telefono: textoOpcional(40),
  direccion: textoOpcional(200),
  ciudad: textoOpcional(80),
  fechaNacimiento: fecha.optional().nullable().or(z.literal('')).transform((v) => (v ? v : null)),
  zonaId: uuid.optional().nullable(),
  referenteId: uuid.optional().nullable(),
  notas: textoOpcional(1000),
  /** Ausente = no se toca (ver la nota de "estado que no se pisa en un PATCH"). */
  activo: z.boolean().optional(),
  tomadoDelPadron,
});
export type EntradaIntegrante = z.infer<typeof esquemaIntegrante>;

export const filtroIntegrantes = paginacion.extend({
  zonaId: uuid.optional(),
  activo: booleanoDeQuery,
});

/**
 * CARGA MASIVA
 *
 * Llega texto pegado de una planilla (o un CSV). Se devuelve una PREVISUALIZACIÓN con el
 * error por fila antes de confirmar: cargar 800 integrantes y enterarse de que la fila 412
 * tenía la cédula pegada al nombre, después de haber escrito 411, no le sirve a nadie.
 */
export const esquemaImportacion = z.object({
  texto: z.string().min(1, 'Pegá las filas o subí el archivo.').max(2_000_000),
  /** Separador: coma, punto y coma o tabulación. Se detecta solo si no viene. */
  separador: z.enum([',', ';', '\t']).optional(),
  /** La primera fila son títulos de columna. */
  conEncabezado: z.boolean().default(true),
  zonaId: uuid.optional().nullable(),
  /** En false (el default) solo devuelve la previsualización; en true escribe. */
  confirmar: z.boolean().default(false),
});
export type EntradaImportacion = z.infer<typeof esquemaImportacion>;

// --- Actividades y participaciones -------------------------------------------------------

export const esquemaActividad = z.object({
  nombre: texto('el nombre de la actividad', 160),
  descripcion: textoOpcional(1000),
  fecha,
  tipoId: uuid.optional().nullable(),
  /** Monto de referencia que se propone para cada integrante. Se puede cambiar persona a persona. */
  montoBase: importe,
  respaldoId: uuid.optional().nullable(),
  notas: textoOpcional(1000),
});
export type EntradaActividad = z.infer<typeof esquemaActividad>;

export const filtroActividades = paginacion.extend({
  estado: z.enum(['BORRADOR', 'ABIERTA', 'CERRADA', 'ANULADA']).optional(),
  tipoId: uuid.optional(),
  desde: fecha.optional(),
  hasta: fecha.optional(),
});

/**
 * Inscribir en lote. O una lista de personas, o todas las de una zona: las dos formas en que
 * se arma una lista de verdad —el que trae su gente por nombre y el que dice "toda la zona 3"—.
 */
export const esquemaInscripcion = z
  .object({
    integranteIds: z.array(uuid).max(5000).optional(),
    zonaId: uuid.optional(),
    /** Si no viene, cada participación arranca con el monto base de la actividad. */
    monto: importe.optional(),
    motivoMonto: textoOpcional(300),
  })
  .refine(
    (v) => (v.integranteIds && v.integranteIds.length > 0) || v.zonaId !== undefined,
    'Elegí a quién inscribir: una lista de personas o una zona.',
  );
export type EntradaInscripcion = z.infer<typeof esquemaInscripcion>;

/**
 * AGREGAR UNA PERSONA A LA LISTA POR SU CÉDULA — el alta de todos los días.
 *
 * Si la cédula ya es de un integrante, se usa ese registro y el nombre que venga se ignora (no se
 * pisa lo cargado desde acá). Si no, se lo da de alta con lo mínimo y queda en la lista en el
 * mismo paso: nadie tiene que ir a otra pantalla a registrarlo antes.
 */
export const esquemaAgregarPersona = z.object({
  documento,
  nombre: texto('el nombre', 120),
  apellido: textoOpcional(120),
  telefono: textoOpcional(40),
  zonaId: uuid.optional().nullable(),
  tomadoDelPadron,
  /** Si no viene, arranca con el monto base de la actividad. */
  monto: importe.optional(),
  motivoMonto: textoOpcional(300),
});
export type EntradaAgregarPersona = z.infer<typeof esquemaAgregarPersona>;

export const esquemaMontoParticipacion = z.object({
  monto: importe,
  motivoMonto: textoOpcional(300),
});

export const esquemaAnulacion = z.object({
  motivo: texto('el motivo', 500),
});

export const filtroParticipaciones = paginacion.extend({
  zonaId: uuid.optional(),
  estado: z.enum(['PENDIENTE', 'PAGADO']).optional(),
});

// --- Pagos ------------------------------------------------------------------------------

export const filtroPagos = paginacion.extend({
  desde: fecha.optional(),
  hasta: fecha.optional(),
  actividadId: uuid.optional(),
  cajeroId: uuid.optional(),
  medioPagoId: uuid.optional(),
  /** Por defecto solo los vigentes: un pago anulado no es plata entregada. */
  anulados: booleanoDeQuery,
});

// --- Catálogos --------------------------------------------------------------------------

/**
 * ESTADO QUE NO SE PISA EN UN PATCH
 * =================================
 *
 * `activo`, `orden` y `exigeReferencia` van **opcionales y sin `.default()`**, y es deliberado.
 *
 * Un `.default(true)` es correcto en un POST y es una trampa en un PATCH: zod lo rellena cuando el
 * cliente no lo manda, así que editarle el nombre a una zona dada de baja la REACTIVARÍA, y editar
 * un medio de pago le apagaría el "exige referencia" sin que nadie lo pida. Opcionales, la semántica
 * queda la que uno espera: **ausente = no se toca**, y el valor inicial de un alta lo pone el
 * `@default` de la columna, que ya está en el esquema de la base.
 *
 * Es la misma trampa que borró el `origenDatos` de los integrantes (ver `origen.ts` en el dominio).
 * Ahí fue un bug de verdad porque el campo no está en ningún formulario y el default se aplicaba
 * SIEMPRE; acá depende de que el formulario mande el campo, así que era una trampa esperando.
 */
export const esquemaZona = z.object({
  nombre: texto('el nombre', 80),
  descripcion: textoOpcional(300),
  orden: enteroNoNegativo.optional(),
  activo: z.boolean().optional(),
});

export const esquemaTipoActividad = z.object({
  nombre: texto('el nombre', 80),
  descripcion: textoOpcional(300),
  /** Monto sugerido al crear una actividad de este tipo. Solo un valor por defecto. */
  /** Ausente = no se toca; `null` = se borra el monto sugerido. Son dos cosas distintas. */
  montoSugerido: importe.optional().nullable(),
  orden: enteroNoNegativo.optional(),
  activo: z.boolean().optional(),
});

export const esquemaMedioPago = z.object({
  nombre: texto('el nombre', 60),
  exigeReferencia: z.boolean().optional(),
  orden: enteroNoNegativo.optional(),
  activo: z.boolean().optional(),
});

export const esquemaEntidad = z.object({
  nombre: texto('el nombre', 120),
  razonSocial: textoOpcional(200),
  ruc: textoOpcional(30),
  direccion: textoOpcional(200),
  telefono: textoOpcional(40),
  email: textoOpcional(120),
  zonaHoraria: z.string().min(1).refine(esZonaValida, 'La zona horaria no existe.'),
  moneda: z.enum(['PYG', 'USD', 'BRL', 'ARS']),
  colorMarca: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'El color tiene que ser hexadecimal, como #3450A1.')
    .optional()
    .nullable(),
  unidadRedondeo: z.number().int().min(1).max(100_000),
  prefijoComprobante: z.string().trim().min(1).max(8),
  pieComprobante: textoOpcional(500),
  exigeVistoBueno: z.boolean(),
  permiteAltaEnMostrador: z.boolean(),
});

// --- Equipo -----------------------------------------------------------------------------

export const esquemaRol = z.object({
  nombre: texto('el nombre', 60),
  descripcion: textoOpcional(300),
  permisos: z.array(z.string()).max(200),
  activo: z.boolean().optional(),
});

export const esquemaAltaUsuario = z.object({
  email: identificadorUsuario,
  documento: documentoOpcional,
  nombre: texto('el nombre', 120),
  rolId: uuid,
  /** Solo si la persona todavía no tiene cuenta en Planilla. */
  clave: z.string().min(10, 'La contraseña tiene que tener al menos 10 caracteres.').optional(),
});

export const esquemaEdicionMiembro = z.object({
  rolId: uuid.optional(),
  activo: z.boolean().optional(),
  documento: documentoOpcional,
});

export const esquemaRestablecerClave = z.object({
  clave: z.string().min(10, 'La contraseña tiene que tener al menos 10 caracteres.'),
});

// --- Auditoría ---------------------------------------------------------------------------

export const filtroAuditoria = paginacion.extend({
  tabla: z.string().max(60).optional(),
  registroId: z.string().max(60).optional(),
  actorId: z.string().max(60).optional(),
  tipo: z.enum(['CAMBIO', 'EVENTO']).optional(),
  evento: z.string().max(60).optional(),
  desde: fecha.optional(),
  hasta: fecha.optional(),
});

// --- Plataforma (RASP) --------------------------------------------------------------------

export const esquemaAltaEntidad = z.object({
  nombre: texto('el nombre', 120),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'El identificador va en minúsculas, con guiones.'),
  planId: uuid.optional().nullable(),
  zonaHoraria: z.string().default('America/Asuncion').refine(esZonaValida, 'La zona horaria no existe.'),
  moneda: z.enum(['PYG', 'USD', 'BRL', 'ARS']).default('PYG'),
  administrador: z.object({
    email: identificadorUsuario,
    documento: documentoOpcional,
    nombre: texto('el nombre', 120),
    clave: z.string().min(10, 'La contraseña tiene que tener al menos 10 caracteres.').optional(),
  }),
});

export const esquemaEdicionEntidadPlataforma = z.object({
  estado: z.enum(['ACTIVA', 'SUSPENDIDA', 'BAJA']).optional(),
  planId: uuid.optional().nullable(),
});

export const esquemaBanderaModulo = z.object({
  habilitado: z.boolean(),
  nota: texto('la nota que explica el cambio', 300),
});

export const esquemaPlanComercial = z.object({
  nombre: texto('el nombre', 60),
  descripcion: textoOpcional(300),
  modulos: z.array(z.string()),
  precioMensual: importe,
  moneda: z.enum(['PYG', 'USD']).default('PYG'),
  activo: z.boolean().optional(),
  orden: enteroNoNegativo.optional(),
});
